import React, { useState } from 'react';
import toast from 'react-hot-toast';

import useEventTracking from '@hooks/useEventTracking';
import useSubscribeToNewsletter from '@hooks/useSubscribeToNewsletter';
import { highlight_path } from '@routes';
import { root_path } from '@routes';
import { TrackingLocation } from '@tracking';
import { FormLocation } from '@utils/enums';
import { parameterize } from '@utils/formatting';

import { Newsletter } from '../../interfaces/homepage';
import { PrimaryButton, TextButton } from '../Button/variants';
import EmailLeadCapture from '../EmailLeadCapture';

interface SubscribeToTheBriefingProps {
  briefingNewsletter: Newsletter;
}

const SubscribeToTheBriefing = ({
  briefingNewsletter: { author, description, id, isLoggedIn, isSubscribed, name },
}: SubscribeToTheBriefingProps) => {
  const [successfullySubscribed, setSuccessfullySubscribed] = useState(false);
  const { mutate: subscribeToNewsletter, isLoading: isSubscribeLoading } =
    useSubscribeToNewsletter();

  const { Track } = useEventTracking({ location: TrackingLocation.BriefingSubscriptionUnit });

  const handleSubscribe = () => {
    try {
      subscribeToNewsletter({ newsletter_id: id });
      setSuccessfullySubscribed(true);
      toast.success("You've successfully subscribed to the newsletter");
    } catch (err) {
      setSuccessfullySubscribed(false);
      toast.error(`There was a problem subscribing to ${name}, please try again later`);
    }
  };

  return (
    <Track>
      <div
        className="mb-80 border-0 border-t border-solid border-primary-black lg:mb-90"
        data-testid="subscribe-to-the-briefing"
      >
        <h2 className="mb-0 mt-32 font-suisse-neue text-body-md text-primary-black lg:text-subheadline-sm">
          Subscribe to {name} by {author}
        </h2>
        <p className="my-10 font-suisse-screen text-disclosure-md text-primary-black">
          {description}
        </p>
        <TextButton
          as="link"
          href={highlight_path(parameterize(name))}
          size="small"
          className="mb-32"
        >
          Read The Briefing
        </TextButton>
        {!isSubscribed && !successfullySubscribed && (
          <>
            {isLoggedIn ? (
              <PrimaryButton
                as="button"
                className="w-full px-24 py-[9px]"
                onClick={handleSubscribe}
                disabled={isSubscribeLoading}
              >
                Subscribe
              </PrimaryButton>
            ) : (
              <EmailLeadCapture
                buttonText="Sign up"
                buttonClassName="w-full lg:w-[140px]"
                inputClassName="w-full"
                isVertical
                formLocation={FormLocation.RightRail}
                redirectUri={root_path()}
                newsletterIds={[id]}
                gdprClassName="md:items-start flex-col lg:flex-row lg:items-center mb-8"
              />
            )}
          </>
        )}
      </div>
    </Track>
  );
};

export default SubscribeToTheBriefing;
